import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ReportEngineService from 'service/ReportEngineService';
import LoadingSpinner from 'component/LoadingSpinner';
import { ReportEngineCreateReq } from 'domain/payload/request/reportEngine/ReportEngineCreateReq';
import { ReportEngineUpdateReq } from 'domain/payload/request/reportEngine/ReportEngineUpdateReq';
import { param } from 'jquery';

const ReportEngineForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useSelector((state: any) => state.auth);

  const reportEngineService = ReportEngineService();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [command, setCommand] = useState('');
  const [parameter, setParameter] = useState<string[]>([]);

  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingDetail(true);
    try {
      const res = await reportEngineService.reportEngineDetail(token, id ?? '0');
      setCode(res.data?.code || '');
      setName(res.data?.name || '');
      setType((res.data?.type || ''));
      setCommand(res.data?.command || '');
      setParameter(res.data?.parameter || []);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail report engine';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const ReportEngineCreate = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    const request: ReportEngineCreateReq = {
      code,
      name,
      type: type,
      command,
      parameter,
    };

    await reportEngineService.reportEngineCreate(token, request);
  };

  const ReportEngineUpdate = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    const request: ReportEngineUpdateReq = {
      id: id!,
      code,
      name,
      type: type,
      command,
      parameter,
    };

    await reportEngineService.reportEngineUpdate(token, request);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoadingSave(true);

    try {
      if (id === '0') {
        await ReportEngineCreate();
        window.alert(`Report Engine "${name}" created successfully!`);
      } else {
        await ReportEngineUpdate();
        window.alert(`Report Engine "${name}" updated successfully!`);
      }
      navigate('/report-engine');
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Error saving data';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingSave(false);
    }
  };

  const handleParameterChange = (index: number, value: string) => {
    const newParams = [...parameter];
    newParams[index] = value;
    setParameter(newParams);
  };

  const addParameter = () => {
    setParameter([...parameter, '']);
  };

  const removeParameter = (index: number) => {
    const newParams = parameter.filter((_, i) => i !== index);
    setParameter(newParams);
  };

  useEffect(() => {
    if (id !== '0') fetchDetail();
  }, [id]);

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4">
          {id === '0' ? 'Add Report Engine' : 'Edit Report Engine'}
        </h4>
        <div className="p-4" style={{ background: 'white' }}>
          {isLoadingDetail ? (
            <LoadingSpinner />
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-sm-6">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Code</label>
                    <input
                      type="text"
                      className="form-control form-control-compact"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Code"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Name</label>
                    <input
                      type="text"
                      className="form-control form-control-compact"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Name"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Type</label>
                    <input
                        type="text"
                        className="form-control form-control-compact"
                        value={type}
                        onChange={(e) => setType(e.target.value.toLowerCase())}
                        placeholder="csv/xlsx"
                        required
                        />
                  </div>
                </div>

                <div className="col-sm-6">
                    <div className="mb-3">
                        <label className="form-label fw-semibold">
                        Parameters
                        </label>

                        {parameter.length === 0 && (
                        <div className="text-muted mb-2 small">
                            No parameters yet.
                        </div>
                        )}

                        {parameter.map((param, index) => (
                        <div key={index} className="d-flex align-items-center mb-2">
                            <input
                            type="text"
                            className="form-control form-control-compact me-2"
                            value={param}
                            onChange={(e) =>
                                handleParameterChange(index, e.target.value)
                            }
                            placeholder={`Parameter ${index + 1}`}
                            />
                            <button
                            type="button"
                            className="btn btn-outline-danger btn-small-compact border-0 ms-1"
                            onClick={() => removeParameter(index)}
                            >
                            <i className="fa-solid fa-trash"></i>
                            </button>
                        </div>
                        ))}

                        <button
                        type="button"
                        className="btn btn-outline-primary btn-sm mt-2"
                        onClick={addParameter}
                        >
                        + Add Parameter
                        </button>
                    </div>
                </div>
                <div className="col-sm-12 mt-3">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Command</label>
                    <textarea
                      className="form-control form-control-compact"
                      rows={5}
                      value={command}
                      onChange={(e) => setCommand(e.target.value)}
                      placeholder="SQL Command"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="w-100 d-flex justify-content-end mt-4">
                <Link to="/report-engine">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    disabled={isLoadingSave}
                  >
                    Cancel
                  </button>
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary ms-2 align-items-center"
                  disabled={isLoadingSave}
                >
                  {isLoadingSave && (
                    <>
                      <LoadingSpinner reverse={true} />
                      <span className="ms-2">Saving...</span>
                    </>
                  )}
                  {!isLoadingSave && 'Save'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportEngineForm;
